import {Alert, Button, CircularProgress, InputAdornment, TextField, Typography} from "@mui/material";
import {FormEvent, useState} from "react";
import {useKeycloak} from "@react-keycloak/web";
import {config} from "../config";
import {LockIcon} from "./OrangeIcons";

type SendStatus = "idle" | "sending" | "success" | "error";

const DEFAULT_ERROR = "Nie udało się wysłać wiadomości. Spróbuj ponownie później.";

function validatePhone(phone: string): string | null {
    if (!phone.trim()) {
        return "Podaj numer telefonu";
    }
    const digits = phone.replace(/\D/g, "");
    if (!/^[+\d\s()-]+$/.test(phone.trim()) || digits.length < 9 || digits.length > 15) {
        return "Podaj poprawny numer telefonu";
    }
    return null;
}

function validateMessage(message: string): string | null {
    return message.trim() ? null : "Treść wiadomości jest wymagana";
}

function LockedField(props: {label: string, value: string}) {
    return (
        <TextField
            className="OrangeContactFormLocked"
            label={props.label}
            value={props.value}
            placeholder="Brak danych w profilu"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            InputProps={{
                readOnly: true,
                endAdornment: (
                    <InputAdornment position="end">
                        <LockIcon fontSize="small"/>
                    </InputAdornment>
                ),
            }}
        />
    )
}

function OrangeContactForm() {
    const {keycloak} = useKeycloak();
    const token = keycloak.tokenParsed;

    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [touched, setTouched] = useState(false);
    const [status, setStatus] = useState<SendStatus>("idle");
    const [error, setError] = useState(DEFAULT_ERROR);

    const phoneError = validatePhone(phone);
    const messageError = validateMessage(message);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (phoneError || messageError) {
            return;
        }

        setStatus("sending");
        let errorMessage = DEFAULT_ERROR;
        try {
            await keycloak.updateToken(30);
            const response = await fetch(`${config.ekuriaServerUrl}/api/v1/orangeContact`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${keycloak.token}`,
                },
                body: JSON.stringify({phone: phone.trim(), message: message.trim()}),
            });
            if (!response.ok) {
                const text = await response.text().catch(() => "");
                if (text && !text.trim().startsWith("{")) {
                    errorMessage = text;
                }
                throw new Error(errorMessage);
            }
            setPhone("");
            setMessage("");
            setTouched(false);
            setStatus("success");
        } catch {
            setError(errorMessage);
            setStatus("error");
        }
    };

    return (
        <form className="OrangeContactForm" onSubmit={handleSubmit} noValidate>
            <Typography variant="h6" className="OrangeContactFormTitle">Jestem zainteresowany ofertą</Typography>
            <Typography className="OrangeContactFormLead">
                Zostaw numer telefonu i krótką wiadomość — dedykowany opiekun Orange skontaktuje się z parafią.
            </Typography>

            <div className="OrangeContactFormLockedGroup">
                <LockedField label="Nazwa parafii" value={token?.displayed_name ?? ""}/>
                <LockedField label="Adres parafii" value={token?.displayed_address ?? ""}/>
                <LockedField label="Adres e-mail" value={token?.email ?? ""}/>
                <Typography className="OrangeContactFormHint">Dane pobrane z konta e-Kuria</Typography>
            </div>

            <TextField
                label="Numer telefonu"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                error={touched && !!phoneError}
                helperText={touched && phoneError}
                required
                fullWidth
                size="small"
                margin="dense"
            />
            <TextField
                label="Treść wiadomości"
                value={message}
                onChange={e => setMessage(e.target.value)}
                error={touched && !!messageError}
                helperText={touched && messageError}
                placeholder="Np. liczba numerów, którymi parafia jest zainteresowana, preferowane godziny kontaktu"
                required
                fullWidth
                multiline
                minRows={4}
                margin="dense"
            />

            {status === "success" &&
                <Alert severity="success" className="OrangeContactFormAlert" onClose={() => setStatus("idle")}>
                    Wiadomość została wysłana. Opiekun Orange wkrótce się z Tobą skontaktuje.
                </Alert>
            }
            {status === "error" &&
                <Alert severity="error" className="OrangeContactFormAlert" onClose={() => setStatus("idle")}>
                    {error}
                </Alert>
            }

            <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={status === "sending"}
                className="OrangeContactFormSubmit"
                startIcon={status === "sending" ? <CircularProgress size={18} color="inherit"/> : undefined}
            >
                {status === "sending" ? "Wysyłanie…" : "Wyślij zapytanie"}
            </Button>
        </form>
    )
}

export default OrangeContactForm;
